<template>
  <div class="rows mobile-cols">
    <div class="half">
      <DataGrid
          :actions-header="$t('settings.car-fleet.add-recipient')"
          :data-model="dataGridModel"
          :data="filteredVehicleRecipients"
          :selected-id="selectedVehicleRecipientId"
          @on-select="selectVehicleRecipientId"
          @on-delete="deleteVehicleRecipientId"
          allow-add
          allow-edit
          allow-delete
      />
    </div>
    <div class="half">
      <div class="data-grid-form cols">
        <div class="cols">
          <div>
            <label for="recipientEmail">
              {{ $t('settings.car-fleet.email') }}
            </label>
            <input
                id="recipientEmail"
                class="stretch"
                :class="{'is-invalid': showStateByField('email') === false}"
                v-model="email"
            />
          </div>
          <div>
            <label for="recipientLanguage">
              {{ $t('settings.car-fleet.language') }}
            </label>
            <select
                id="recipientLanguage"
                :class="['stretch', {'is-invalid': showStateByField('language') === false}]"
                v-model="language"
            >
              <option value="en">🇬🇧 English</option>
              <option value="bg">🇧🇬 Български</option>
            </select>
          </div>
          <div>
            <label for="recipientVehicles">
              {{ $t('settings.car-fleet.vehicles') }}
            </label>
            <select
                id="recipientVehicles"
                multiple
                :size="vehiclesDisplayRows"
                :class="['stretch', {'is-invalid': showStateByField('vehicles') === false}]"
                v-model="selectedVehicles"
            >
              <optgroup v-for="group in groups" v-bind:key="group.id" :label="group.name">
                <option v-for="vehicle in getVehiclesByGroupId(group.id)" v-bind:key="vehicle.id" :value="vehicle.id">
                  {{ vehicle.fullName }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="mb-10">
            <label for="recipientUser">
              {{ $t('generic.user') }}
            </label>
            <div class="cols align-items-start">
              <multiselect id="recipientUser"
                           v-model="selectedUser"
                           :options="users"
                           :close-on-select="true"
                           :clear-on-select="false"
                           :preserve-search="true"
                           :show-labels="false"
                           :placeholder="$t('settings.generic.select-items')"
                           :show-no-results="false"
                           :show-no-options="false"
                           label="name" track-by="id"
                           class="tracking-multiselect">
                <template #option="props">
                  <div class="tracking-multiselect-with-checkbox">
                    <span class="width-20">
                      <i v-if="selectedUser?.id === props.option.id" class="bi bi-check-lg width-15"></i>
                    </span>
                    <span class="option__title pr-5">{{ props.option.name }}</span>
                    <span class="option__title" v-if="getUserCompany(props.option.id) === null">
                      {{ $t('settings.on-board.users.form.company-no-data') }}
                    </span>
                    <span class="option__title" v-else>{{ $t('generic.company') }}: {{ getUserCompany(props.option.id)?.name }}</span>
                  </div>
                </template>
                <template #singleLabel>
                  <span class="option__title pr-5">
                    {{ selectedUser.name }}
                  </span>
                  <span class="option__title" v-if="getUserCompany(selectedUser?.id) === null">
                      {{ $t('settings.on-board.users.form.company-no-data') }}
                    </span>
                  <span class="option__title" v-else>
                      {{ $t('generic.company') }}: {{ getUserCompany(selectedUser?.id)?.name }}
                    </span>
                </template>
              </multiselect>
              <button class="transparent pl-5" @click="clearUserSelection">
                <span class="text-green">{{ $t('generic.clear') }}</span>
              </button>
            </div>
          </div>
          <div>
            <button type="submit" class="button-save" @click="save">
              {{ $t('generic.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {actions} from '@/assets/js/vue/vuex/actions';
import notification from '@/assets/js/notification';
import {mapState} from 'vuex';
import {dataAggregator, entityDataManager} from '@/assets/js/data-manipulation';
import DataGrid from '@/components/pages/partials/DataGrid';
import Multiselect from "vue-multiselect";

export default {
  name: 'Recipients',
  components: {Multiselect, DataGrid},
  props: {
    searchToken: String,
    selectedVehicleRecipientId: [Number, null],
  },
  data() {
    return {
      fieldStates: {},

      email: null,
      language: 'en',
      selectedUser: null,
      selectedVehicles: [],
    };
  },
  computed: {
    ...mapState(['vehicleRecipients', 'groups', 'devices', 'vehicles', 'lastErrorDetails', 'companies', 'users']),
    dataGridModel() {
      return {
        headers: {
          [this.$t('settings.car-fleet.email')]: 'email',
          [this.$t('settings.car-fleet.language')]: 'language',
        },
      };
    },
    vehiclesDisplayRows() {
      return Math.min(10, this.vehicles.length);
    },
    filteredVehicleRecipients() {
      return dataAggregator.filterListByToken(this.vehicleRecipients, ['email'], this.searchToken);
    },
  },
  watch: {
    selectedVehicleRecipientId(id) {
      this.fillFormByVehicleRecipientId(id);
    },
  },
  mounted() {
    this.$store.dispatch(actions.GET_VEHICLE_RECIPIENTS).catch(e => notification.error(this.$t(e.message)));
  },
  methods: {
    selectVehicleRecipientId(id) {
      this.$emit('on-select-vehicle-recipient', id);
    },
    fillFormByVehicleRecipientId(vehicleRecipientId) {
      if (vehicleRecipientId === null) {
        this.email = null;
        this.language = 'en';
        this.selectedVehicles = [];
        this.selectedUser = null;

        return;
      }

      const vehicleRecipient = entityDataManager.getById(this.vehicleRecipients, vehicleRecipientId);

      if (vehicleRecipient) {
        this.email = vehicleRecipient.email;
        this.language = vehicleRecipient.language;
        this.selectedVehicles = vehicleRecipient.vehicles;
        this.selectedUser = entityDataManager.getEntityByField(this.users, 'id', vehicleRecipient.userId);
      }
    },
    showDirtyFields() {
      this.fieldStates = {};

      if (this.lastErrorDetails === null) {
        return;
      }

      this.lastErrorDetails.forEach(error => {
        if (error.field !== undefined) {
          this.fieldStates[error.field] = false;
        }
      });
    },
    showStateByField(field) {
      return entityDataManager.getStateByField(this.fieldStates, field);
    },
    getVehiclesByGroupId(groupId) {
      return entityDataManager.getVehiclesByGroupId(this.devices, this.vehicles, groupId);
    },
    getUserCompany(userId) {
      return entityDataManager.getCompanyByUserId(this.companies, userId)
    },
    clearUserSelection() {
      this.selectedUser = null;
    },
    async save() {
      const payload = {
        id: this.selectedVehicleRecipientId,
        email: this.email,
        language: this.language,
        userId: this.selectedUser?.id,
        vehicles: this.selectedVehicles,
      };

      const vehicleRecipient = await this.saveVehicleRecipient(payload);

      if (vehicleRecipient === null) {
        return;
      }

      await this.$store.dispatch(actions.GET_VEHICLE_RECIPIENTS).catch(e => notification.error(this.$t(e.message)));

      this.selectVehicleRecipientId(vehicleRecipient.id);
    },
    async saveVehicleRecipient(payload) {
      let resultVehicleRecipient = null;

      const action = payload.id === null ? actions.CREATE_VEHICLE_RECIPIENT : actions.EDIT_VEHICLE_RECIPIENT;

      await this.$store.dispatch(action, payload).
          then(
              vehicleRecipient => {
                resultVehicleRecipient = vehicleRecipient;
                notification.success(this.$t('generic.save.success'));
              },
          ).
          catch(e => notification.error(this.$t(e.message))).
          finally(() => this.showDirtyFields());

      return resultVehicleRecipient;
    },
    deleteVehicleRecipientId(vehicleRecipientId) {
      this.selectVehicleRecipientId(vehicleRecipientId);

      if (confirm(this.$t('generic.delete.confirm'))) {
        this.$store.dispatch(actions.DELETE_VEHICLE_RECIPIENT, {id: this.selectedVehicleRecipientId}).
            then(() => {
              this.selectVehicleRecipientId(null);
              this.$store.dispatch(actions.GET_VEHICLE_RECIPIENTS).catch(e => notification.error(this.$t(e.message)));
              notification.success(this.$t('generic.delete.success'));
            }).
            catch(e => notification.error(this.$t(e.message)));
      }
    },
  },
};
</script>
